/**
 * Linkify bare entity ids in chat markdown prose / GFM table cells as
 * trodo://entity anchors (ChatPage click delegation opens the drawer).
 */
import React, { Children, cloneElement, isValidElement, Fragment } from "react";
import { EntityLink } from "shared/components/chat/ChatRichBlocks";
import { findEntityTokens } from "shared/components/chat/chatEntityLinkUtils";

export { UUID_RE, COLUMN_ENTITY_KIND, normColKey, entityKindForColumn, findEntityTokens } from "shared/components/chat/chatEntityLinkUtils";

export function linkifyPlainText(text, LinkComponent = EntityLink) {
  if (!text) return null;
  const tokens = findEntityTokens(text);
  if (!tokens.length) return text;

  const parts = [];
  let last = 0;
  tokens.forEach((t, i) => {
    if (t.index > last) parts.push({ type: "text", value: text.slice(last, t.index), key: `t-${i}` });
    parts.push({ type: "link", kind: t.kind, id: t.id, key: `l-${i}` });
    last = t.index + t.length;
  });
  if (last < text.length) parts.push({ type: "text", value: text.slice(last), key: "tail" });

  return parts.map((p) => {
    if (p.type === "text") return <Fragment key={p.key}>{p.value}</Fragment>;
    return (
      <LinkComponent key={p.key} kind={p.kind} id={p.id}>
        {p.id}
      </LinkComponent>
    );
  });
}

/** Recursively linkify strings inside markdown-rendered nodes (incl. <p> inside <td>). */
export function linkifyMarkdownChildren(children, LinkComponent = EntityLink) {
  return Children.map(children, (child, i) => {
    if (child == null || child === false) return child;
    if (typeof child === "string") return <Fragment key={i}>{linkifyPlainText(child, LinkComponent)}</Fragment>;
    if (typeof child === "number") return child;
    if (Array.isArray(child)) {
      return <Fragment key={i}>{linkifyMarkdownChildren(child, LinkComponent)}</Fragment>;
    }
    if (isValidElement(child)) {
      if (child.type === "a") return child;
      if (child.props?.children != null) {
        return cloneElement(
          child,
          { key: child.key ?? i },
          linkifyMarkdownChildren(child.props.children, LinkComponent),
        );
      }
    }
    return child;
  });
}
